import { InvoiceItem } from './InvoiceItem';
import { CompanyAddressWithContact, ICompanyAddressWithContact } from '@lib/address';
import { IInvoiceDraft, IInvoiceDraftPrice } from './IInvoiceDraftDto';
import { IimFilterableRecord } from '@iim-angular/components';
import { IMoney } from '../../types/IMoney';
import { CustomerDepartmentDto } from '@lib/department/ui';

export class CreateInvoiceCharge {
    id?: string;
    name: string;
    description: string;
    articleNumberSupplier: string;
    articleNumberCustomer: string;
    price: IMoney;
    taxInPercent: number;
    internalNote: string;
    chargeType: string;
    taxAmount?: number;
}

export class StandaloneInvoiceDraft {
    invoiceNumber: string;
    sectionId: string;
    remark: string;
    documentDate: string;
    deliveryDate: string;
    supplier?: {
        id: string;
        name: string;
        supplierNumber?: string | null;
    };
    companyCodeId: string | null;
    department?: {
        name: string;
        code: string;
        description: string;
        index: string;
    };
    paymentDate: string;
    paymentUntil: string;
    comment: string;
    currency: string;
    items: InvoiceItem[];
    invoiceAddress?: ICompanyAddressWithContact;
    charges: CreateInvoiceCharge[];
    files: File[];
    price: IInvoiceDraftPrice | null;

    get hasReceiptRelation(): boolean {
        return this.items.some((item) => !!item.deliveryRelation);
    }

    get currencyForAutocomplete(): IimFilterableRecord<string> {
        return new IimFilterableRecord(this.currency);
    }

    constructor(dto: IInvoiceDraft) {
        this.invoiceNumber = dto.invoiceNumber ?? '';
        this.remark = dto.remark ?? '';
        this.sectionId = dto.sectionId ?? '';
        this.companyCodeId = dto.companyCodeId ?? null;
        this.documentDate = dto.invoiceDate ?? '';
        this.deliveryDate = '';
        if (dto.supplierCompanyId && dto.supplierCompanyName) {
            this.supplier = {
                id: dto.supplierCompanyId,
                name: dto.supplierCompanyName,
                supplierNumber: dto.supplierNumber,
            };
        }
        this.department = dto.department;
        this.paymentDate = dto.paymentDate ?? '';
        this.paymentUntil = dto.paymentUntil ?? '';
        this.comment = dto.comment ?? '';
        this.currency = dto.currency || 'EUR';
        this.items = dto.items.map((item) => new InvoiceItem(item));
        if (dto.invoiceAddress) {
            this.invoiceAddress = CompanyAddressWithContact.fromCustomerAddressDto(dto.invoiceAddress);
        }

        this.charges = (dto.charges ?? []).map((charge) => {
            return {
                ...charge,
            } as CreateInvoiceCharge;
        });

        this.files = (dto.files ?? []).map((file) => {
            return {
                ...file,
            };
        });
        this.price = dto.price
            ? {
                  totalNetPrice: dto.price.totalNetPrice,
                  totalGrossPrice: dto.price.totalGrossPrice,
                  currency: dto.price.currency,
                  taxes: (dto.price.taxes ?? []).map((tax) => ({
                      percent: tax.percent,
                      amount: tax.amount,
                  })),
              }
            : null;
    }

    public withAddress(address: ICompanyAddressWithContact): StandaloneInvoiceDraft {
        // Create a new instance with the same properties
        const newInvoice = new StandaloneInvoiceDraft({
            invoiceNumber: this.invoiceNumber,
            remark: this.remark,
            sectionId: this.sectionId,
            supplierCompanyId: this.supplier?.id,
            supplierCompanyName: this.supplier?.name,
            companyCodeId: this.companyCodeId ?? null,
            department: this.department,
            currency: this.currency,
            items: [], // Empty array initially
            invoiceDate: '',
            paymentDate: '',
            paymentUntil: '',
            charges: this.charges,
            files: this.files,
        });

        // Copy all properties from the current instance
        newInvoice.documentDate = this.documentDate;
        newInvoice.deliveryDate = this.deliveryDate;
        newInvoice.paymentDate = this.paymentDate;
        newInvoice.paymentUntil = this.paymentUntil;
        newInvoice.comment = this.comment;
        newInvoice.items = this.items.slice(); // Copy the items array
        newInvoice.charges = this.charges.slice();
        newInvoice.price = this.price
            ? {
                  ...this.price,
                  taxes: this.price.taxes.map((tax) => ({ ...tax })),
              }
            : null;

        // Set the new address
        newInvoice.invoiceAddress = address;

        return newInvoice;
    }

    public withoutAddress(): StandaloneInvoiceDraft {
        const newInvoice = new StandaloneInvoiceDraft({
            invoiceNumber: this.invoiceNumber,
            remark: this.remark,
            sectionId: this.sectionId,
            supplierCompanyId: this.supplier?.id,
            supplierCompanyName: this.supplier?.name,
            companyCodeId: this.companyCodeId ?? null,
            department: this.department,
            currency: this.currency,
            items: [],
            invoiceDate: '',
            paymentDate: '',
            paymentUntil: '',
            charges: this.charges,
            files: this.files,
        });

        newInvoice.documentDate = this.documentDate;
        newInvoice.deliveryDate = this.deliveryDate;
        newInvoice.paymentDate = this.paymentDate;
        newInvoice.paymentUntil = this.paymentUntil;
        newInvoice.comment = this.comment;
        newInvoice.items = this.items.slice();
        newInvoice.charges = this.charges.slice();
        newInvoice.price = this.price
            ? {
                  ...this.price,
                  taxes: this.price.taxes.map((tax) => ({ ...tax })),
              }
            : null;
        newInvoice.invoiceAddress = undefined;

        return newInvoice;
    }

    public withSupplier(supplier: { id: string; name: string; supplierNumber?: string | null }): StandaloneInvoiceDraft {
        // Create a new instance with the same properties
        const newInvoice = new StandaloneInvoiceDraft({
            invoiceNumber: this.invoiceNumber,
            remark: this.remark,
            sectionId: this.sectionId,
            supplierCompanyId: supplier.id,
            supplierCompanyName: supplier.name,
            supplierNumber: supplier.supplierNumber,
            companyCodeId: this.companyCodeId ?? null,
            department: this.department,
            currency: this.currency,
            items: [], // Empty array initially
            invoiceDate: '',
            paymentDate: '',
            paymentUntil: '',
            charges: this.charges,
            files: this.files,
        });

        // Copy all properties from the current instance
        newInvoice.documentDate = this.documentDate;
        newInvoice.deliveryDate = this.deliveryDate;
        newInvoice.paymentDate = this.paymentDate;
        newInvoice.paymentUntil = this.paymentUntil;
        newInvoice.comment = this.comment;
        newInvoice.items = this.items.slice(); // Copy the items array
        newInvoice.charges = this.charges.slice();
        newInvoice.price = this.price
            ? {
                  ...this.price,
                  taxes: this.price.taxes.map((tax) => ({ ...tax })),
              }
            : null;

        // Set the new address
        newInvoice.invoiceAddress = this.invoiceAddress;

        return newInvoice;
    }

    public withoutSupplier(): StandaloneInvoiceDraft {
        const newInvoice = new StandaloneInvoiceDraft({
            invoiceNumber: this.invoiceNumber,
            remark: this.remark,
            sectionId: this.sectionId,
            supplierCompanyId: undefined,
            supplierCompanyName: undefined,
            companyCodeId: this.companyCodeId ?? null,
            department: this.department,
            currency: this.currency,
            items: [],
            invoiceDate: '',
            paymentDate: '',
            paymentUntil: '',
            charges: this.charges,
            files: this.files,
        });

        newInvoice.documentDate = this.documentDate;
        newInvoice.deliveryDate = this.deliveryDate;
        newInvoice.paymentDate = this.paymentDate;
        newInvoice.paymentUntil = this.paymentUntil;
        newInvoice.comment = this.comment;
        newInvoice.items = this.items.slice();
        newInvoice.charges = this.charges.slice();
        newInvoice.price = this.price
            ? {
                  ...this.price,
                  taxes: this.price.taxes.map((tax) => ({ ...tax })),
              }
            : null;
        newInvoice.invoiceAddress = this.invoiceAddress;

        return newInvoice;
    }

    public withDepartment(department: CustomerDepartmentDto): StandaloneInvoiceDraft {
        // Create a new instance with the same properties
        const newInvoice = new StandaloneInvoiceDraft({
            invoiceNumber: this.invoiceNumber,
            remark: this.remark,
            sectionId: this.sectionId,
            supplierCompanyId: this.supplier?.id,
            supplierCompanyName: this.supplier?.name,
            companyCodeId: this.companyCodeId ?? null,
            department: {
                name: department.name,
                code: department.code,
                index: department.index,
                description: department.description,
            },
            currency: this.currency,
            items: [], // Empty array initially
            invoiceDate: '',
            paymentDate: '',
            paymentUntil: '',
            charges: this.charges,
            files: this.files,
        });

        // Copy all properties from the current instance
        newInvoice.documentDate = this.documentDate;
        newInvoice.deliveryDate = this.deliveryDate;
        newInvoice.paymentDate = this.paymentDate;
        newInvoice.paymentUntil = this.paymentUntil;
        newInvoice.comment = this.comment;
        newInvoice.items = this.items.slice(); // Copy the items array
        newInvoice.charges = this.charges.slice();
        newInvoice.price = this.price
            ? {
                  ...this.price,
                  taxes: this.price.taxes.map((tax) => ({ ...tax })),
              }
            : null;

        // Set the new address
        newInvoice.invoiceAddress = this.invoiceAddress;

        return newInvoice;
    }

    public withCompanyCode(companyCodeId: string | null): StandaloneInvoiceDraft {
        const newInvoice = new StandaloneInvoiceDraft({
            invoiceNumber: this.invoiceNumber,
            remark: this.remark,
            sectionId: this.sectionId,
            supplierCompanyId: this.supplier?.id,
            supplierCompanyName: this.supplier?.name,
            companyCodeId,
            department: this.department,
            currency: this.currency,
            items: [],
            invoiceDate: '',
            paymentDate: '',
            paymentUntil: '',
            charges: this.charges,
            files: this.files,
        });

        newInvoice.documentDate = this.documentDate;
        newInvoice.deliveryDate = this.deliveryDate;
        newInvoice.paymentDate = this.paymentDate;
        newInvoice.paymentUntil = this.paymentUntil;
        newInvoice.comment = this.comment;
        newInvoice.items = this.items.slice();
        newInvoice.charges = this.charges.slice();
        newInvoice.price = this.price
            ? {
                  ...this.price,
                  taxes: this.price.taxes.map((tax) => ({ ...tax })),
              }
            : null;
        newInvoice.invoiceAddress = this.invoiceAddress;

        return newInvoice;
    }
}
